import { useI18n } from '../utils/i18n';
import { useToast } from './ToastProvider';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import Modal from './ui/Modal';
import ListState from './ui/ListState';
import { Mail, Paperclip, Plus, Eye, EyeOff, Link } from 'lucide-react';
import EmailContextMenu from './EmailContextMenu';
import EmailDetail from './EmailDetail';
import TicketCustomerField from './TicketCustomerField';
import { CalenderMarkerHeaderSelect } from '../utils/calenderMarkerUtils';
import apiService from '../services/apiService';
import { findCustomerIdByEmail } from '../utils/customerCache';
import { ticketPasstZurSuche } from '../utils/suchFilter';
import { useFeature } from '../utils/featuresCache';
import '../styles/EmailList.css';
import '../styles/Dashboard.css';

const EmailList = ({ emails, onEmailUpdate, filterSettings = {}, maxDisplayEmails = null, disableBackgroundAutoAssign = false, compact = false, status = 'ready', onRetry = null }) => {
  const [contextMenu, setContextMenu] = useState(null);
  const [showEmailDetail, setShowEmailDetail] = useState(false);
  const [selectedEmailDetail, setSelectedEmailDetail] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [openTickets, setOpenTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCreateTicketForm, setShowCreateTicketForm] = useState(false);
  const [availableEmails, setAvailableEmails] = useState([]);
  const [linkedEmailIds, setLinkedEmailIds] = useState([]);
  const [ticketFilterText, setTicketFilterText] = useState('');
  const [newTicketData, setNewTicketData] = useState({
    title: '',
    description: '',
    priority: 'normal',
    category: 'E-Mail',
    calenderMarker: null,
    assignedTo: '',
    customerId: null,
    contactId: null
  });
  const { getLocale, t } = useI18n();
  const kundenAktiv = useFeature('customers'); // #251: Platzhalter nennt Kunde nur, wenn der Kundenstamm an ist
  const { pushToast } = useToast();

  // Filter emails based on filter settings  
  const { filteredEmails, totalFilteredCount } = useMemo(() => {
    if (!emails || emails.length === 0) return { filteredEmails: [], totalFilteredCount: 0 };
    
    let filtered = [...emails];
    
    // Filter 1: Hide read emails
    if (filterSettings.hideReadEmails) {
      filtered = filtered.filter(email => !email.isRead);
    }
    
    // Filter 2: Only emails from last X hours
    if (filterSettings.enableHoursFilter && filterSettings.hoursFilter > 0) {
      const hoursAgo = new Date();
      hoursAgo.setHours(hoursAgo.getHours() - filterSettings.hoursFilter);
      
      filtered = filtered.filter(email => {
        const emailDate = new Date(email.receivedDateTime);
        return emailDate >= hoursAgo;
      });
    }
    
    // Filter 3: Keyword filtering (sender and subject)
    if (filterSettings.keywords && filterSettings.keywords.trim()) {
      const keywords = filterSettings.keywords
        .split('\n')
        .map(k => k.trim().toLowerCase())
        .filter(k => k.length > 0);
      
      if (keywords.length > 0) {
        filtered = filtered.filter(email => {
          const subject = (email.subject || '').toLowerCase();
          const senderName = (email.from?.emailAddress?.name || '').toLowerCase();
          const senderAddress = (email.from?.emailAddress?.address || '').toLowerCase();
          
          // Check if any keyword matches in subject or sender
          const hasKeyword = keywords.some(keyword => 
            subject.includes(keyword) || 
            senderName.includes(keyword) || 
            senderAddress.includes(keyword)
          );
          
          // Filter out emails that contain keywords (blacklist approach)
          return !hasKeyword;
        });
      }
    }
    
    const totalFilteredCount = filtered.length;
    
    // Apply display limit after filtering (so filters work on full list)
    if (maxDisplayEmails && filtered.length > maxDisplayEmails) {
      filtered = filtered.slice(0, maxDisplayEmails);
    }
    
    return { filteredEmails: filtered, totalFilteredCount };
  }, [emails, filterSettings, maxDisplayEmails]);

  
  // Get user's timezone automatically
  const getUserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Handle right-click context menu
  const handleContextMenu = (e, email) => {
    e.preventDefault();
    setContextMenu({
      email,
      position: { x: e.clientX, y: e.clientY }
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleEmailClick = (email) => {
    const mailboxAddress = email.mailboxAddress || email.__mailbox || undefined;
    const hydratedEmail = mailboxAddress && !email.mailboxAddress ? { ...email, mailboxAddress } : email;
    setSelectedEmailDetail(hydratedEmail);
    setShowEmailDetail(true);
    
    // Note: Email is NOT automatically marked as read
    // Users must explicitly use the toggle button to change read status
  };

  const handleCloseEmailDetail = () => {
    setShowEmailDetail(false);
    setSelectedEmailDetail(null);
  };

  const handleMarkAsRead = async (emailId, isRead, mailboxAddress = undefined) => {
    try {
      await apiService.markEmailAsRead(emailId, isRead, mailboxAddress);
      console.log(`📧 Email ${emailId} marked as ${isRead ? 'read' : 'unread'}`);
      
      // Notify parent component to refresh email data
      if (onEmailUpdate) {
        onEmailUpdate();
      }
    } catch (error) {
      console.error('Error marking email as read:', error);
    }
  };



  const loadOpenTickets = async () => {
    setLoadingTickets(true);
    try {
      const [ticketsResponse, emailsResponse] = await Promise.all([
        apiService.getOpenTickets(),
        apiService.getAvailableEmails()
      ]);
      
      setOpenTickets(ticketsResponse.tickets || []);
      setAvailableEmails(emailsResponse.emails || []);
    } catch (error) {
      console.error('Error loading open tickets:', error);
      setOpenTickets([]);
      setAvailableEmails([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  const loadLinkedItems = async () => {
    try {
      // Request only linked email ids to reduce load
      const response = await apiService.getLinkedItems({ scope: 'email', timeoutMs: 5000 });
      setLinkedEmailIds(response.linkedEmailIds || []);
    } catch (error) {
      console.error('📧 [EMAIL LIST] Error loading linked items:', error);
      setLinkedEmailIds([]);
    }
  };

  // #432: EIN Effekt statt drei. Vorher hingen drei Effekte an Mount, an der
  // Array-Referenz `emails` und an `emails.length` -- drei identische Anfragen
  // beim Öffnen, zwei bei jeder Aktualisierung.
  //
  // Bewusst an der Array-REFERENZ, nicht an der Menge der Mail-IDs: Ein neues
  // Array ist das Signal der Eltern, dass sich Verknüpfungen geändert haben
  // können. Das Dashboard setzt es nach jedem onEmailUpdate ausdrücklich
  // (`setEmails(prev => [...prev])`, etwa nach einer Zuordnung in EmailDetail),
  // und der Minuten-Poll zeigt so Zuordnungen, die anderswo entstanden sind
  // (Postfachüberwachung, Kollegen). An der ID-Menge blieben beide unsichtbar.
  useEffect(() => {
    loadLinkedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bewusst nur an `emails` (#432)
  }, [emails]);

  // Render EmailDetail as a Portal so it persists even if the list becomes empty
  const emailDetailPortal = showEmailDetail ? createPortal(
    <EmailDetail 
      email={selectedEmailDetail}
      onClose={handleCloseEmailDetail}
      onEmailUpdate={onEmailUpdate}
    />,
    document.body
  ) : null;

  const handleShowTicketModal = () => {
    if (contextMenu?.email) {
      setSelectedEmail(contextMenu.email);
      setShowTicketModal(true);
      setTicketFilterText(''); // Reset filter when opening modal
      loadOpenTickets();
    }
  };

  // Filter tickets based on search text
  // #251: Nummer, Titel, Kunde, Ansprechpartner -- gemeinsamer Helfer
  const filteredTickets = openTickets.filter(ticket => ticketPasstZurSuche(ticket, ticketFilterText));

  const handleAddEmailToTicket = async (ticketId) => {
    if (!selectedEmail) {
      pushToast({ type: 'error', message: t('email.errors.noneSelected', 'Fehler: Keine E-Mail ausgewählt') });
      return;
    }
    
    setLoading(true);
    try {
      // Sende E-Mail-Daten mit für automatisches Caching
      await apiService.addEmailToTicket(ticketId, selectedEmail.id, null, selectedEmail);
      setShowTicketModal(false);
      setSelectedEmail(null);
      
      // Reload linked items to update the linked symbol
      await loadLinkedItems();
      
      // Trigger ticket list refresh for correct sorting
      if (onEmailUpdate) {
        await onEmailUpdate();
      }
    } catch (error) {
      console.error('Error adding email to ticket:', error);
      pushToast({ type: 'error', message: t('email.errors.addToTicket', 'Fehler beim Hinzufügen der E-Mail zum Ticket: {{details}}', { details: error.message }) });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewTicket = () => {
    if (selectedEmail) {
      setNewTicketData({
        title: selectedEmail.subject || 'Neues Ticket',
        description: selectedEmail.bodyPreview || '',
        priority: 'normal',
        category: 'E-Mail',
        calenderMarker: null,
        assignedTo: '',
        customerId: null,
        contactId: null
      });
      // #210: per Absender-Domäne erkannten Kunden vorbelegen — sichtbar und
      // überschreibbar (nur setzen, solange der Benutzer nichts gewählt hat)
      const sender = selectedEmail.from?.emailAddress?.address || selectedEmail.fromAddress || null;
      findCustomerIdByEmail(sender).then(cid => {
        if (cid) setNewTicketData(prev => (prev.customerId ? prev : { ...prev, customerId: cid }));
      });
    }
    setShowCreateTicketForm(true);
  };

  const handleSubmitNewTicket = async (e) => {
    e.preventDefault();
    if (!selectedEmail) {
      pushToast({ type: 'error', message: t('email.errors.noneSelected', 'Fehler: Keine E-Mail ausgewählt') });
      return;
    }
    
    setLoading(true);
    try {
      // Sende E-Mail-Daten mit für automatisches Caching
      await apiService.createTicketFromEmail(selectedEmail.id, newTicketData, selectedEmail);
      setShowTicketModal(false);
      setShowCreateTicketForm(false);
      setSelectedEmail(null);
      setNewTicketData({
        title: '',
        description: '',
        priority: 'normal',
        category: 'E-Mail',
        calenderMarker: null,
        assignedTo: '',
        customerId: null,
        contactId: null
      });
      // Reload linked items to update the linked symbol
      await loadLinkedItems();
      
      // Trigger ticket list refresh for correct sorting
      if (onEmailUpdate) {
        await onEmailUpdate();
      }
      // Dashboard-Ticketlisten sofort aktualisieren
      try { window.dispatchEvent(new Event('dashboardEventRefresh')); } catch (_) {}
    } catch (error) {
      console.error('Error creating ticket from email:', error);
      pushToast({ type: 'error', message: t('ticket.createErrorDetails', 'Fehler beim Erstellen des Tickets: {{details}}', { details: error.message }) });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCreateTicket = () => {
    setShowCreateTicketForm(false);
    setNewTicketData({
      title: '',
      description: '',
      priority: 'normal',
      category: 'E-Mail',
      calenderMarker: null,
      assignedTo: '',
      customerId: null,
      contactId: null
    });
  };

  // #436: Mails, die in dieser Sitzung schon geprüft wurden. Ohne dieses
  // Gedächtnis ging bei jeder Änderung der Liste dieselbe Menge erneut an den
  // Server -- am 24.09.2026 dieselben 29 Mails elfmal pro Stunde. Ein Betreff
  // ändert sich nicht, eine Mail ohne Ticketnummer bleibt also eine. Erneut
  // versucht wird nur, was gerade an anderer Stelle verarbeitet wurde.
  const gepruefteMailIdsRef = useRef(new Set());

  // Automatic background email assignment
  useEffect(() => {
    const performBackgroundAutoAssignment = async () => {
      // Skip background auto assignment when disabled (e.g., active search)
      if (disableBackgroundAutoAssign) {
        return;
      }
      // Only run if we have emails and linked email IDs are loaded
      if (!filteredEmails || filteredEmails.length === 0 || linkedEmailIds.length === 0) {
        return;
      }

      // Filter unlinked emails that could potentially be assigned
      const geprueft = gepruefteMailIdsRef.current;
      const unlinkedEmails = filteredEmails.filter(email => !linkedEmailIds.includes(email.id) && !geprueft.has(email.id));
      
      if (unlinkedEmails.length === 0) {
        return;
      }

      try {
        // #436: das Postfach je Mail mitschicken -- ohne suchte der Server jede
        // Mail im eigenen Postfach, und Mails aus zusätzlichen Postfächern
        // konnten nie zugeordnet werden.
        const eintraege = unlinkedEmails.map(email => ({
          id: email.id,
          mailboxAddress: email.mailboxAddress || email.__mailbox || undefined
        }));
        const result = await apiService.batchAutoAssignEmails(eintraege);
        for (const r of (result.results || [])) {
          if (r.emailId && r.action !== 'already_processing') geprueft.add(r.emailId);
        }

        if (result.summary.assigned > 0) {
          console.log(`📧 [EMAIL LIST] Background auto-assignment completed: ${result.summary.assigned} emails assigned`);
          
          // Refresh linked items to reflect the changes
          await loadLinkedItems();

          // Trigger email update if callback provided
          if (onEmailUpdate) {
            setTimeout(() => onEmailUpdate(), 1000); // Small delay to avoid rapid refreshes
          }
        }

      } catch (error) {
        // Silently handle errors in background processing
        console.error('📧 [EMAIL LIST] Background auto-assignment error:', error);
      }
    };

    // Debounce the auto-assignment to avoid too frequent calls
    const timeoutId = setTimeout(performBackgroundAutoAssignment, 2000); // 2 second delay

    return () => clearTimeout(timeoutId);
  }, [filteredEmails, linkedEmailIds, disableBackgroundAutoAssign]); // Run when emails or linked status changes


  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(null);
    };

    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  const formatDate = (dateString) => {
    // FIXED: Treat the datetime as UTC and convert properly  
    const utcDateTime = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDateTime);
    const userTimeZone = getUserTimeZone();
    return date.toLocaleDateString(getLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTimeZone
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    
    // Entferne HTML-Tags und CID-Referenzen für die Vorschau
    let cleanText = text
      .replace(/<[^>]*>/g, '') // Entferne HTML-Tags
      .replace(/cid:[^"'\s>]+/gi, '[Bild]') // Ersetze CID-Referenzen durch [Bild]
      .replace(/\s+/g, ' ') // Normalisiere Leerzeichen
      .trim();
    
    if (cleanText.length <= maxLength) return cleanText;
    return cleanText.substring(0, maxLength) + '...';
  };

  const getImportance = (importance) => {
    switch (importance) {
      case 'high':
        return '🔴';
      case 'low':
        return '🔵';
      default:
        return '';
    }
  };

  if (!emails || emails.length === 0) {
    // #256: Ohne Ladezustand behauptete die Spalte beim Kaltstart "Keine
    // E-Mails gefunden", obwohl der Abruf noch lief oder fehlgeschlagen war.
    if (status === 'loading') {
      return (
        <>
          {emailDetailPortal}
          <ListState variant="loading" />
        </>
      );
    }
    if (status === 'error') {
      return (
        <>
          {emailDetailPortal}
          <ListState variant="error" onRetry={onRetry} />
        </>
      );
    }
    return (
      <>
        {emailDetailPortal}
        <div className="loading">
          <Mail size={48} style={{ opacity: 0.3 }} />
          <p>{t('email.list.noEmails', 'Keine E-Mails gefunden')}</p>
        </div>
      </>
    );
  }

  if (filteredEmails.length === 0) {
    const hasActiveFilters = filterSettings.hideReadEmails || 
                           filterSettings.enableHoursFilter || 
                           (filterSettings.keywords && filterSettings.keywords.trim());
    
    return (
      <>
        {emailDetailPortal}
        <div className="loading">
          <Mail size={48} style={{ opacity: 0.3 }} />
          <p>{hasActiveFilters ? t('email.list.noneMatchFilters', 'Keine E-Mails entsprechen den aktiven Filtern') : t('email.list.noEmails', 'Keine E-Mails gefunden')}</p>
          {hasActiveFilters && (
            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-placeholder)', marginTop: '8px' }}>
              {t('email.list.adjustFilters', 'Entfernen Sie Filter oder passen Sie die Filterkriterien an')}
            </p>
          )}
        </div>
      </>
    );
  }

  return (
    <div>

    <ul className="email-list">
      {filteredEmails.map((email) => (
          <li 
            key={email.id} 
            className="email-item"
            onContextMenu={(e) => handleContextMenu(e, email)}
            onClick={() => handleEmailClick(email)}
            style={{ 
              cursor: 'pointer',
              opacity: linkedEmailIds.includes(email.id) ? 0.2 : 1
            }}
          >
          <div className="email-subject" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              {getImportance(email.importance)}
              {email.subject || t('email.subject.none', '(Kein Betreff)')}
              {email.hasAttachments && (
                <Paperclip size={16} style={{ marginLeft: '5px', color: 'var(--color-text-muted)' }} />
              )}
            </div>

          </div>
          {compact ? (
            <div className="email-meta-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
              <div className="email-from" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Von: {email.from?.emailAddress?.name || email.from?.emailAddress?.address || 'Unbekannt'}
              </div>
              <div className="email-date" style={{ marginLeft: 8, color: 'var(--color-text-muted)', fontSize: '14px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                {formatDate(email.receivedDateTime)}
              </div>
            </div>
          ) : (
            <>
              <div className="email-from">
                Von: {email.from?.emailAddress?.name || email.from?.emailAddress?.address || 'Unbekannt'}
              </div>
              <div className="email-preview" style={{ 
                color: 'var(--color-text-muted)', 
                fontSize: '14px', 
                marginTop: '5px',
                lineHeight: '1.4'
              }}>
                {truncateText(email.bodyPreview)}
              </div>
              <div className="email-date">
                {formatDate(email.receivedDateTime)}
              </div>
            </>
          )}
          <div style={{ 
            display: 'flex', 
            gap: '10px', 
            marginTop: '5px', 
            fontSize: '12px', 
            color: 'var(--color-text-muted)',
            alignItems: 'center'
          }}>
            {email.isRead ? (
              <span>✓ {t('email.read', 'Gelesen')}</span>
            ) : (
              <span style={{ fontWeight: 'bold', color: 'var(--color-primary)' }}>● {t('email.unread', 'Ungelesen')}</span>
            )}
            {email.flag?.flagStatus === 'flagged' && (
              <span>🚩 Markiert</span>
            )}
            
            {/* Read/Unread Toggle Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                const mailboxAddress = email.mailboxAddress || email.__mailbox || undefined;
                handleMarkAsRead(email.id, !email.isRead, mailboxAddress);
              }}
              className={`email-read-toggle ${email.isRead ? 'read' : 'unread'}`}
              title={email.isRead ? t('email.markAsUnread', 'Als ungelesen markieren') : t('email.markAsRead', 'Als gelesen markieren')}
            >
              {email.isRead ? <EyeOff size={12} /> : <Eye size={12} />}
              {email.isRead ? t('email.unread', 'Ungelesen') : t('email.read', 'Gelesen')}
            </button>
            
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              {email.__mailbox && (
                <span title="Postfach">{email.__mailbox}</span>
              )}
              {linkedEmailIds.includes(email.id) && (
                <Link size={16} style={{ color: 'var(--color-primary)' }} title={t('common.linkedToTicket', 'Mit Ticket verknüpft')} />
              )}
            </span>
          </div>
        </li>
      ))}
    </ul>
    
    {/* Show indicator if more emails are available */}
    {maxDisplayEmails && emails && totalFilteredCount > 0 && (
      <div style={{ 
        textAlign: 'center', 
        padding: '10px', 
        fontSize: '0.8rem', 
        color: 'var(--color-text-muted)',
        borderTop: '1px solid var(--color-border-light)',
        background: 'var(--color-bg)'
      }}>
        {totalFilteredCount > maxDisplayEmails 
          ? `Zeige ${maxDisplayEmails} von ${totalFilteredCount} E-Mails • ${totalFilteredCount - maxDisplayEmails} weitere verfügbar`
          : `Alle ${totalFilteredCount} E-Mails angezeigt`
        }
      </div>
    )}
    
    {contextMenu && (
      <EmailContextMenu
        email={contextMenu.email}
        position={contextMenu.position}
        onClose={handleCloseContextMenu}
        onTicketCreated={handleCloseContextMenu}
        onShowTicketModal={handleShowTicketModal}
      />
    )}
    

    {/* TICKET MODAL */}
    <Modal
      open={showTicketModal}
      onClose={() => { setShowTicketModal(false); setSelectedEmail(null); }}
      title={<><Link size={24} /><h3>{t('email.addToTicket.modalTitle', 'E-Mail zu Ticket hinzufügen')}</h3>{showCreateTicketForm && (<CalenderMarkerHeaderSelect value={newTicketData.calenderMarker} onChange={(value) => setNewTicketData({...newTicketData, calenderMarker: value})} />)}</>}
      ariaLabel={t('email.addToTicket.modalTitle', 'E-Mail zu Ticket hinzufügen')}
      maxWidth="600px"
      footer={<button type="button" onClick={() => { setShowTicketModal(false); setSelectedEmail(null); }} className="btn btn-secondary">{t('common.cancel', 'Abbrechen')}</button>}
    >

          
          {showCreateTicketForm ? (
            <div className="create-ticket-form">
              <h4>{t('ticket.create.new', 'Neues Ticket erstellen:')}</h4>
              <form onSubmit={handleSubmitNewTicket}>
                {/* #210: Kunde/Ansprechpartner wie in der Ticketmaske (#104);
                    per Domäne erkannte Zuordnung ist vorbelegt und überschreibbar.
                    Funktionales setState: Kundenwechsel feuert zwei Resets (#105). */}
                <TicketCustomerField
                  value={newTicketData.customerId}
                  onChange={(customerId) => setNewTicketData(prev => ({ ...prev, customerId }))}
                  contactValue={newTicketData.contactId}
                  onContactChange={(contactId) => setNewTicketData(prev => ({ ...prev, contactId }))}
                  inputId="email-list-ticket-customer"
                  contactInputId="email-list-ticket-contact"
                />
                <div className="form-group">
                  <label>{t('form.title', 'Titel')}</label>
                  <input
                    type="text"
                    value={newTicketData.title}
                    onChange={(e) => setNewTicketData({...newTicketData, title: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>{t('form.description', 'Beschreibung')}</label>
                  <textarea
                    value={newTicketData.description}
                    onChange={(e) => setNewTicketData({...newTicketData, description: e.target.value})}
                    rows="4"
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('form.priority', 'Priorität')}</label>
                    <select
                      value={newTicketData.priority}
                      onChange={(e) => setNewTicketData({...newTicketData, priority: e.target.value})}
                    >
                      <option value="niedrig">{t('priority.low', 'Niedrig')}</option>
                      <option value="normal">{t('priority.normal', 'Normal')}</option>
                      <option value="hoch">{t('priority.high', 'Hoch')}</option>
                      <option value="kritisch">{t('priority.critical', 'Kritisch')}</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>{t('form.assignedTo', 'Zugewiesen an')}</label>
                    <select
                      value={newTicketData.assignedTo}
                      onChange={(e) => setNewTicketData({...newTicketData, assignedTo: e.target.value})}
                    >
                      <option value="">{t('common.unassigned', 'Nicht zugewiesen')}</option>
                      {availableEmails.map(email => (
                        <option key={email} value={email}>{email}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                
                
                <div className="form-actions">
                  <button type="button" onClick={handleCancelCreateTicket} className="btn btn-secondary">
                    {t('common.back', 'Zurück')}
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? t('common.creating', 'Erstelle...') : t('ticket.create.submit', 'Ticket erstellen')}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="ticket-selector">
              <h4>{t('ticket.addToExisting', 'Zu vorhandenem Ticket hinzufügen:')}</h4>
              
              {/* Filter input */}
              <div className="ticket-filter">
                <input
                  type="text"
                  placeholder={(kundenAktiv ? t('ticket.filter.placeholderCustomer', 'Nach Ticketnummer, Titel, Kunde oder Ansprechpartner suchen...') : t('ticket.filter.placeholder', 'Nach Ticketnummer oder Titel suchen...'))}
                  value={ticketFilterText}
                  onChange={(e) => setTicketFilterText(e.target.value)}
                  className="ticket-filter-input"
                />
              </div>
              
              {loadingTickets ? (
                <p>{t('ticket.loading', 'Lade Tickets...')}</p>
              ) : openTickets.length === 0 ? (
                <p>{t('ticket.noneOpen', 'Keine offenen Tickets gefunden.')}</p>
              ) : filteredTickets.length === 0 ? (
                <p>{t('ticket.noneMatchFilter', 'Keine Tickets gefunden, die dem Filter entsprechen.')}</p>
              ) : (
                <div className="ticket-list-scrollable">
                  {filteredTickets.map(ticket => (
                    <div key={ticket.id} className="ticket-item-compact">
                      <div className="ticket-info-compact">
                        <div className="ticket-header-compact">
                          <span className="ticket-number-compact">#{ticket.ticketNumber}</span>
                          <div className="ticket-badges-compact">
                            <span className={`status-badge-compact status-${ticket.status}`}>
                              {ticket.status}
                            </span>
                            <span className={`priority-badge-compact priority-${ticket.priority}`}>
                              {ticket.priority}
                            </span>
                          </div>
                        </div>
                        <div className="ticket-title-compact">{ticket.title}</div>
                      </div>
                      <button 
                        onClick={() => handleAddEmailToTicket(ticket.id)}
                        className="btn btn-secondary btn-compact"
                        disabled={loading}
                      >
                        {loading ? t('common.adding', 'Füge hinzu...') : t('common.add', 'Hinzufügen')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="create-ticket-option">
                <button onClick={handleCreateNewTicket} className="btn btn-primary">
                  <Plus size={16} />
                  {t('ticket.create.button', 'Neues Ticket erstellen')}
                </button>
              </div>
            </div>
          )}
    </Modal>

    {/* EMAIL DETAIL MODAL */}
    {emailDetailPortal}


  </div>
  );
};

export default EmailList; 